import React from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useTranslation } from 'react-i18next';
import { TimeSlotFormData } from './timeSlotFormSchema';

interface TimeSelectionFieldsProps {
  control: Control<TimeSlotFormData>;
  watch: UseFormWatch<TimeSlotFormData>;
  setValue: UseFormSetValue<TimeSlotFormData>;
}

const TimeSelectionFields: React.FC<TimeSelectionFieldsProps> = ({
  control,
  watch,
  setValue,
}) => {
  const { t } = useTranslation();
  const autoEndTime = watch('autoEndTime');
  const startTime = watch('startTime');

  // Auto-calculate end time when start time changes and auto mode is enabled
  React.useEffect(() => {
    if (autoEndTime && startTime) {
      const [hours, minutes] = startTime.split(':');
      const startDate = new Date();
      startDate.setHours(parseInt(hours), parseInt(minutes));
      startDate.setHours(startDate.getHours() + 1); // Add one hour

      const endTimeString = startDate.toTimeString().slice(0, 5);
      setValue('endTime', endTimeString);
    }
  }, [autoEndTime, startTime, setValue]);

  return (
    <>
      {/* Auto End Time Option */}
      <FormField
        control={control}
        name="autoEndTime"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center space-x-2 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal">
                {t('schedule.form.autoCalculateEndTime')}
              </FormLabel>
            </div>
          </FormItem>
        )}
      />

      {/* Time Selection */}
      <div className="grid sm:grid-cols-2 gap-4">
        <FormField
          control={control}
          name="startTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('schedule.startTime')}</FormLabel>
              <FormControl>
                <Input type="time" {...field} className="text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="endTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('schedule.endTime')}</FormLabel>
              <FormControl>
                <Input
                  type="time"
                  {...field}
                  className="text-base"
                  disabled={autoEndTime}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};

export default TimeSelectionFields;
